import { useState, useEffect } from 'react';
import { ArrowLeft, Package, Clock, CheckCircle, XCircle, User, Phone, MapPin, Star, LogOut, DollarSign, TrendingUp, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import ETAModal from '@/components/ETAModal';
import ChatWidget from '@/components/ChatWidget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BarberDashboard = () => {
  const navigate = useNavigate();
  const [barber, setBarber] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    confirmed: 0,
    in_progress: 0,
    completed: 0,
    earnings: 0,
    today_bookings: 0
  });
  const [showETAModal, setShowETAModal] = useState(false);
  const [selectedBookingForETA, setSelectedBookingForETA] = useState(null);
  const [activeBookingForChat, setActiveBookingForChat] = useState(null);

  useEffect(() => {
    const savedBarber = localStorage.getItem('barbergo_barber');
    if (!savedBarber) {
      navigate('/');
      return;
    }
    setBarber(JSON.parse(savedBarber));
    fetchBookings();
    
    const interval = setInterval(fetchBookings, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchBookings = async () => {
    try {
      const savedBarber = localStorage.getItem('barbergo_barber');
      if (!savedBarber) return;
      
      const barberData = JSON.parse(savedBarber);
      
      const response = await axios.get(`${API}/bookings`);
      const allBookings = response.data;
      
      // Show all bookings that need barber attention (requested status = new booking requests)
      setBookings(allBookings);
      
      const today = new Date().toISOString().split('T')[0];
      const stats = {
        total: allBookings.length,
        // "requested" is the new status for bookings waiting for barber to accept
        pending: allBookings.filter(b => b.status === 'requested' || b.status === 'pending').length,
        confirmed: allBookings.filter(b => b.status === 'accepted' || b.status === 'confirmed').length,
        in_progress: allBookings.filter(b => b.status === 'in_progress').length,
        completed: allBookings.filter(b => b.status === 'completed').length,
        earnings: allBookings
          .filter(b => b.status === 'completed')
          .reduce((sum, b) => sum + (b.estimated_price * 0.8), 0),
        today_bookings: allBookings.filter(b => b.booking_date === today).length
      };
      setStats(stats);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Failed to load bookings');
      setLoading(false);
    }
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      await axios.patch(`${API}/bookings/${bookingId}/status?status=${newStatus}`);
      toast.success(`Booking ${newStatus}!`);
      fetchBookings();
    } catch (error) {
      console.error('Error updating booking:', error);
      toast.error('Failed to update booking');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('barbergo_barber');
    toast.success('Logged out successfully');
    navigate('/');
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'requested': return 'bg-amber-500/10 text-amber-500 border-amber-500/20';
      case 'accepted': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'pending': return 'bg-amber-500/10 text-amber-500 border-amber-500/20';
      case 'confirmed': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'in_progress': return 'bg-violet-500/10 text-violet-500 border-violet-500/20';
      case 'completed': return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20';
      case 'cancelled': return 'bg-red-500/10 text-red-500 border-red-500/20';
      default: return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
    }
  };

  const getFilteredBookings = () => {
    if (activeTab === 'all') return bookings;
    // Map "pending" tab to show "requested" status bookings
    if (activeTab === 'pending') {
      return bookings.filter(b => b.status === 'requested' || b.status === 'pending');
    }
    // Map "confirmed" tab to show "accepted" status bookings
    if (activeTab === 'confirmed') {
      return bookings.filter(b => b.status === 'accepted' || b.status === 'confirmed');
    }
    return bookings.filter(b => b.status === activeTab);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)] dark:bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#00C167] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-[var(--text-primary)] text-xl">Loading dashboard...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] dark:bg-[#0A0A0A]" data-testid="barber-dashboard">
      {/* Enhanced Header */}
      <div className="bg-[var(--bg-secondary)] dark:bg-[#111111] border-b border-[var(--border-color)] sticky top-0 z-40 backdrop-blur-xl">
        <div className="container py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                data-testid="back-button"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Home
              </Button>
              
              {barber && (
                <div className="hidden md:flex items-center gap-3 px-4 py-2 rounded-lg bg-[var(--bg-primary)] dark:bg-[#0A0A0A] border border-[var(--border-color)]">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00C167] to-[#009E54] flex items-center justify-center text-white font-bold">
                    {barber.full_name?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="font-bold text-[var(--text-primary)] text-sm">{barber.full_name}</div>
                    <div className="text-xs text-[var(--text-secondary)] flex items-center gap-1">
                      <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                      {barber.rating?.toFixed(1)} • {barber.city}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:border-red-500"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="container py-8">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-4xl font-black tracking-tight text-[var(--text-primary)] mb-2">Order Management</h1>
          <p className="text-[var(--text-secondary)] text-lg">Accept bookings, manage services, and track your earnings</p>
        </div>

        {/* Enhanced Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <div className="bg-gradient-to-br from-[#00C167] to-[#009E54] rounded-xl p-6 text-white shadow-lg shadow-[#00C167]/20">
            <DollarSign className="w-8 h-8 mb-3 opacity-80" />
            <div className="text-3xl font-black mb-1">₦{stats.earnings.toLocaleString()}</div>
            <div className="text-sm opacity-90">Total Earnings</div>
          </div>

          <div className="bg-[var(--bg-secondary)] dark:bg-[#111111] border border-[var(--border-color)] rounded-xl p-6 hover:border-[#00C167] transition-all">
            <Package className="w-6 h-6 mb-3 text-[var(--text-secondary)]" />
            <div className="text-3xl font-black text-[var(--text-primary)] mb-1">{stats.total}</div>
            <div className="text-sm text-[var(--text-secondary)]">Total Orders</div>
          </div>

          <div className="bg-[var(--bg-secondary)] dark:bg-[#111111] border border-amber-500/20 rounded-xl p-6 hover:border-amber-500 transition-all">
            <Clock className="w-6 h-6 mb-3 text-amber-500" />
            <div className="text-3xl font-black text-amber-500 mb-1">{stats.pending}</div>
            <div className="text-sm text-[var(--text-secondary)]">Pending</div>
          </div>

          <div className="bg-[var(--bg-secondary)] dark:bg-[#111111] border border-blue-500/20 rounded-xl p-6 hover:border-blue-500 transition-all">
            <CheckCircle className="w-6 h-6 mb-3 text-blue-500" />
            <div className="text-3xl font-black text-blue-500 mb-1">{stats.confirmed}</div>
            <div className="text-sm text-[var(--text-secondary)]">Confirmed</div>
          </div>

          <div className="bg-[var(--bg-secondary)] dark:bg-[#111111] border border-violet-500/20 rounded-xl p-6 hover:border-violet-500 transition-all">
            <TrendingUp className="w-6 h-6 mb-3 text-violet-500" />
            <div className="text-3xl font-black text-violet-500 mb-1">{stats.in_progress}</div>
            <div className="text-sm text-[var(--text-secondary)]">In Progress</div>
          </div>

          <div className="bg-[var(--bg-secondary)] dark:bg-[#111111] border border-emerald-500/20 rounded-xl p-6 hover:border-emerald-500 transition-all">
            <CheckCircle className="w-6 h-6 mb-3 text-emerald-500" />
            <div className="text-3xl font-black text-emerald-500 mb-1">{stats.completed}</div>
            <div className="text-sm text-[var(--text-secondary)]">Completed</div>
          </div>
        </div>

        {/* Orders List */}
        <div className="bg-[var(--bg-secondary)] dark:bg-[#111111] border border-[var(--border-color)] rounded-2xl overflow-hidden shadow-xl">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="border-b border-[var(--border-color)] px-6 py-4 bg-[var(--bg-primary)] dark:bg-[#0A0A0A]">
              <TabsList className="w-full justify-start gap-2">
                <TabsTrigger value="all" data-testid="tab-all" className="data-[state=active]:bg-[#00C167] data-[state=active]:text-white">
                  All <span className="ml-2 px-2 py-0.5 rounded-full bg-white/20 text-xs">{stats.total}</span>
                </TabsTrigger>
                <TabsTrigger value="pending" data-testid="tab-pending" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">
                  Pending <span className="ml-2 px-2 py-0.5 rounded-full bg-white/20 text-xs">{stats.pending}</span>
                </TabsTrigger>
                <TabsTrigger value="confirmed" data-testid="tab-confirmed" className="data-[state=active]:bg-blue-500 data-[state=active]:text-white">
                  Confirmed <span className="ml-2 px-2 py-0.5 rounded-full bg-white/20 text-xs">{stats.confirmed}</span>
                </TabsTrigger>
                <TabsTrigger value="in_progress" data-testid="tab-in-progress" className="data-[state=active]:bg-violet-500 data-[state=active]:text-white">
                  In Progress <span className="ml-2 px-2 py-0.5 rounded-full bg-white/20 text-xs">{stats.in_progress}</span>
                </TabsTrigger>
                <TabsTrigger value="completed" data-testid="tab-completed" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white">
                  Completed <span className="ml-2 px-2 py-0.5 rounded-full bg-white/20 text-xs">{stats.completed}</span>
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value={activeTab} className="p-6">
              {getFilteredBookings().length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-20 h-20 rounded-full bg-[var(--bg-primary)] dark:bg-[#0A0A0A] border-2 border-dashed border-[var(--border-color)] flex items-center justify-center mx-auto mb-4">
                    <Package className="w-10 h-10 text-[var(--text-secondary)]" />
                  </div>
                  <p className="text-[var(--text-secondary)] text-lg font-medium">No {activeTab} orders found</p>
                  <p className="text-[var(--text-secondary)] text-sm mt-1">Check back later for new bookings</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {getFilteredBookings().map(booking => (
                    <div
                      key={booking.id}
                      data-testid={`booking-card-${booking.id}`}
                      className="group border-2 border-[var(--border-color)] rounded-xl p-6 hover:border-[#00C167] hover:shadow-lg transition-all bg-[var(--bg-primary)] dark:bg-[#0A0A0A]"
                    >
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                        {/* Left: Service Info */}
                        <div className="flex-1 space-y-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="text-xl font-bold text-[var(--text-primary)]">{booking.service_name}</h3>
                                <span className={`px-3 py-1 rounded-full text-xs font-bold border-2 ${getStatusColor(booking.status)}`}>
                                  {booking.status === 'requested' ? 'NEW REQUEST' : 
                                   booking.status === 'accepted' ? 'ACCEPTED' :
                                   booking.status.toUpperCase().replace('_', ' ')}
                                </span>
                              </div>
                              <div className="flex items-center gap-4 text-sm text-[var(--text-secondary)]">
                                <div className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                                  <span>{booking.booking_date}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  <span>{booking.booking_time}</span>
                                </div>
                                <div className="px-2 py-1 rounded bg-[#00C167]/10 text-[#00C167] font-semibold">
                                  ₦{booking.estimated_price?.toLocaleString()}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Customer Details */}
                          <div className="grid md:grid-cols-2 gap-4 p-4 rounded-lg bg-[var(--bg-secondary)] dark:bg-[#111111] border border-[var(--border-color)]">
                            <div>
                              <div className="text-xs text-[var(--text-secondary)] mb-1 uppercase tracking-wide">Customer</div>
                              <div className="flex items-center gap-2 text-[var(--text-primary)] font-medium">
                                <User className="w-4 h-4 text-[#00C167]" />
                                <span>{booking.customer_name}</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)] mt-1">
                                <Phone className="w-3 h-3" />
                                <span>{booking.customer_phone}</span>
                              </div>
                            </div>
                            <div>
                              <div className="text-xs text-[var(--text-secondary)] mb-1 uppercase tracking-wide">Location</div>
                              <div className="flex items-start gap-2 text-[var(--text-primary)] font-medium">
                                <MapPin className="w-4 h-4 text-[#00C167] mt-0.5 flex-shrink-0" />
                                <span className="text-sm">{booking.address}</span>
                              </div>
                              <div className="mt-1 text-xs text-[var(--text-secondary)] capitalize">
                                {booking.location_type} service
                              </div>
                            </div>
                          </div>

                          {booking.notes && (
                            <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/10">
                              <div className="text-xs text-blue-400 font-semibold mb-1">Special Instructions</div>
                              <p className="text-sm text-[var(--text-primary)]">{booking.notes}</p>
                            </div>
                          )}
                        </div>

                        {/* Right: Actions */}
                        <div className="flex md:flex-col gap-2 md:min-w-[180px]">
                          {(booking.status === 'requested' || booking.status === 'pending') && (
                            <>
                              <Button
                                onClick={() => {
                                  setSelectedBookingForETA(booking);
                                  setShowETAModal(true);
                                }}
                                className="flex-1 bg-[#00C167] hover:bg-[#009E54] text-white font-semibold"
                                data-testid={`accept-${booking.id}`}
                              >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Accept
                              </Button>
                              <Button
                                onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                                variant="outline"
                                className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10"
                                data-testid={`reject-${booking.id}`}
                              >
                                <XCircle className="w-4 h-4 mr-2" />
                                Decline
                              </Button>
                            </>
                          )}
                          {(booking.status === 'accepted' || booking.status === 'confirmed') && (
                            <>
                              <Button
                                onClick={() => updateBookingStatus(booking.id, 'in_progress')}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                                data-testid={`start-${booking.id}`}
                              >
                                <TrendingUp className="w-4 h-4 mr-2" />
                                Start Service
                              </Button>
                              <Button
                                onClick={() => setActiveBookingForChat(booking)}
                                variant="outline"
                                className="w-full border-[#00C167]/30 text-[#00C167] hover:bg-[#00C167]/10 mt-2"
                                data-testid={`chat-${booking.id}`}
                              >
                                <Phone className="w-4 h-4 mr-2" />
                                Chat with Customer
                              </Button>
                            </>
                          )}
                          {booking.status === 'in_progress' && (
                            <>
                              <Button
                                onClick={() => updateBookingStatus(booking.id, 'completed')}
                                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                                data-testid={`complete-${booking.id}`}
                              >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Mark Complete
                              </Button>
                              <Button
                                onClick={() => setActiveBookingForChat(booking)}
                                variant="outline"
                                className="w-full border-[#00C167]/30 text-[#00C167] hover:bg-[#00C167]/10 mt-2"
                              >
                                <Phone className="w-4 h-4 mr-2" />
                                Chat with Customer
                              </Button>
                            </>
                          )}
                          {booking.status === 'completed' && (
                            <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-center">
                              <CheckCircle className="w-6 h-6 text-emerald-500 mx-auto mb-1" />
                              <div className="text-xs text-emerald-500 font-semibold">Completed</div>
                              <div className="text-xs text-[var(--text-secondary)] mt-1">
                                Earned: ₦{(booking.estimated_price * 0.8).toLocaleString()}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* ETA Modal */}
      <ETAModal
        booking={selectedBookingForETA}
        barber={barber}
        open={showETAModal}
        onClose={() => {
          setShowETAModal(false);
          setSelectedBookingForETA(null);
        }}
        onAccepted={() => {
          fetchBookings();
          setShowETAModal(false);
          setSelectedBookingForETA(null);
        }}
      />

      {/* Chat Widget */}
      {activeBookingForChat && barber && (
        <ChatWidget
          booking={activeBookingForChat}
          user={barber}
          userType="barber"
        />
      )}
    </div>
  );
};

export default BarberDashboard;